import React, { useCallback } from 'react';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import { generateMIReport, getMIJobStatus, getMIReportByJob } from '../services/api';
import { useJobReport } from '../hooks/useJobReport';
import { MIReportView } from './mi/MIReportView';
import { ReportEmpty, ReportGenerating, ReportLoading, RegenerateButton, RegeneratingNote } from './report/ReportStates';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MESSAGES = [
  'Benchmarking salary and total compensation...',
  'Sizing the talent pool and who is open to a move...',
  'Profiling the ideal candidate and feeder titles...',
  'Finding companies whose people fit this role...',
  'Building the screening scorecard and knockout questions...',
  'Writing the candidate pitch and objection handling...',
  'Mapping competitors, staffing firms and nearby markets...',
  'Finalizing offer strategy and recruiter recommendations...',
];

const MarketIntelligenceTab = ({ jobId, jobTitle, jobDescription, jobLocation, jobType, companyName, readOnly }) => {
  const start = useCallback(() => {
    if (!jobDescription) {
      toast.error('Job description is required for market intelligence');
      return null;
    }
    return generateMIReport({
      job_title: jobTitle, job_location: jobLocation || 'United States', work_type: jobType === 'remote' ? 'remote' : 'onsite',
      job_description: jobDescription, company_name: companyName || '', hirefast_job_id: jobId,
    });
  }, [jobId, jobTitle, jobDescription, jobLocation, jobType, companyName]);

  const r = useJobReport({ jobId, fetchByJob: getMIReportByJob, fetchStatus: getMIJobStatus, start, name: 'Market intelligence' });

  if (r.loading) return <ReportLoading />;
  if (!r.data && r.running) return <ReportGenerating Icon={Globe} tone="purple" title="Generating Market Intelligence" messages={MESSAGES} testid="mi-generating" />;
  if (!r.data) {
    return (
      <ReportEmpty Icon={Globe} tone="purple" title="Market Intelligence" failed={r.failed} onGenerate={r.generate} readOnly={readOnly}
        label="Generate Market Intelligence" testid="mi-empty-state" buttonTestid="mi-generate-btn"
        text="Salary benchmarks, talent pool sizing, who to target and where they work today, a screening scorecard, candidate pitch and offer strategy for this role." />
    );
  }
  return (
    <div className="space-y-4">
      {r.running && <RegeneratingNote testid="mi-regenerating-note" />}
      <MIReportView data={r.data} pdfUrl={`${BACKEND_URL}/api/mi/report/${r.data.report_id}/pdf`}
        actions={!readOnly && <RegenerateButton onClick={r.generate} running={r.running} label="Regenerate Report" testid="mi-regenerate-btn" />} />
    </div>
  );
};

export default MarketIntelligenceTab;
